import React, { useMemo } from 'react';
import { CheckCircle2, HelpCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES } from '@crm/shared/real-estate-plan-presets';
import { Link } from 'react-router-dom';
import { DemoRequestForm } from '../components/forms/DemoRequestForm';
import { HomeSectionHeader } from '../components/home/HomeSectionHeader';
import { homeButtonClasses } from '../components/home/HomeButton';
import { HOME_SURFACES } from '../components/home/homeSurfaces';
import { MarketingPageHero } from '../components/marketing/MarketingPageHero';
import {
  marketingCardClass,
  marketingPlanBadgeClass,
} from '../components/marketing/marketingSurfaces';
import {
  commercialProducts,
  type ProductCode,
} from '../config/commercialProducts';
import { type PlanCode } from '../config/commercialPlans';
import {
  findLegacyProductMarketingRoute,
  findSegmentMarketingRoute,
  MARKETPLACE_SITE_URL,
  type LegacyProductRouteKey,
  type SegmentCode,
  type SegmentMarketingRoute,
} from '../config/marketingRoutes';
import { resolveCrmRegisterUrl } from '../lib/appUrls';
import {
  buildSegmentStructuredData,
  type SegmentFaq,
} from '../lib/marketingPrerender';
import { usePageMetadata, useStructuredData } from '../lib/seo';
import { cn } from '../lib/utils';

interface IncludedModule {
  code: ProductCode;
  name: string;
  value: string;
}

type ProductRoutePageProps =
  | { routeKey: LegacyProductRouteKey; segmentCode?: never }
  | { segmentCode: SegmentCode; routeKey?: never };

const PRODUCT_ROUTE_ITEM_INDEXES = [0, 1, 2] as const;

function readStringList(value: unknown, label: string): string[] {
  if (
    !Array.isArray(value) ||
    !value.every((entry) => typeof entry === 'string')
  ) {
    throw new Error(`Missing segment string list: ${label}`);
  }

  return value;
}

function readFaqList(value: unknown, label: string): SegmentFaq[] {
  if (!Array.isArray(value)) {
    throw new Error(`Missing segment FAQ list: ${label}`);
  }

  return value.map((entry, index) => {
    if (
      !entry ||
      typeof entry !== 'object' ||
      !('question' in entry) ||
      !('answer' in entry) ||
      typeof entry.question !== 'string' ||
      typeof entry.answer !== 'string'
    ) {
      throw new Error(`Invalid segment FAQ at ${label}.${index}`);
    }

    return {
      question: entry.question,
      answer: entry.answer,
    };
  });
}

function resolvePrimaryHref(): string {
  return resolveCrmRegisterUrl(
    import.meta.env.VITE_CRM_REGISTER_URL,
    import.meta.env.VITE_CRM_LOGIN_URL
  );
}

export function shouldUseDocumentNavigationForProductHref(
  href: string
): boolean {
  return (
    href === '/peru' ||
    href.startsWith('/peru/') ||
    href === MARKETPLACE_SITE_URL ||
    href.startsWith(`${MARKETPLACE_SITE_URL}/`)
  );
}

function isPaidPlanCode(
  planCode: PlanCode
): planCode is Exclude<PlanCode, 'free'> {
  return planCode !== 'free';
}

function resolveIncludedModules(
  route: SegmentMarketingRoute,
  translate: (key: string) => string
): IncludedModule[] {
  return REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES[route.planCode].map(
    (moduleCode) => {
      const product = commercialProducts.find(
        (candidate) => candidate.code === moduleCode
      );

      if (!product) {
        throw new Error(
          `Plan ${route.planCode} references unknown marketing module ${moduleCode}`
        );
      }

      return {
        code: product.code,
        name: translate(product.nameKey),
        value: translate(product.valueKey),
      };
    }
  );
}

function LegacyProductPage({
  routeKey,
}: {
  routeKey: LegacyProductRouteKey;
}): React.JSX.Element {
  const content = findLegacyProductMarketingRoute(routeKey);
  const { t } = useTranslation('common');
  const translationBase = `productRoutes.${content.translationKey}`;
  const primaryHref = resolvePrimaryHref();
  const badge = t(`${translationBase}.badge`);
  const title = t(`${translationBase}.title`);
  const subtitle = t(`${translationBase}.subtitle`);
  const primaryCta = t(`${translationBase}.primaryCta`);
  const secondaryCta = t(`${translationBase}.secondaryCta`);

  usePageMetadata({
    title: `${title} | Inmueble.red`,
    description: subtitle,
    path: content.path,
  });

  return (
    <div className="flex flex-col bg-brand-bg">
      <MarketingPageHero
        badge={badge}
        title={title}
        subtitle={subtitle}
        actions={
          <>
            <a href={primaryHref} className={homeButtonClasses('accent', 'lg')}>
              {primaryCta}
            </a>
            {shouldUseDocumentNavigationForProductHref(
              content.secondaryHref
            ) ? (
              <a
                href={content.secondaryHref}
                className={homeButtonClasses('outline', 'lg')}
              >
                {secondaryCta}
              </a>
            ) : (
              <Link
                to={content.secondaryHref}
                className={homeButtonClasses('outline', 'lg')}
              >
                {secondaryCta}
              </Link>
            )}
          </>
        }
      />

      <section className={HOME_SURFACES.ivory}>
        <div className="section-padding">
          <HomeSectionHeader
            badge={badge}
            title={t('productRoutes.includes.title')}
            subtitle={t('productRoutes.includes.subtitle')}
            className="mb-10"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {PRODUCT_ROUTE_ITEM_INDEXES.map((index) => {
              const item = t(`${translationBase}.items.${index}`);
              return (
                <article key={item} className={cn(marketingCardClass, 'p-7')}>
                  <p className="text-base leading-relaxed text-[var(--ir-muted)]">
                    {item}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function SegmentProductPage({
  segmentCode,
}: {
  segmentCode: SegmentCode;
}): React.JSX.Element {
  const { t } = useTranslation(['segments', 'pricing', 'modules', 'common']);
  const route = findSegmentMarketingRoute(segmentCode);
  const translationBase = `segments:segments.${route.segmentCode}`;
  const title = t(route.titleKey);
  const description = t(route.metaDescriptionKey);
  const subtitle = t(`${translationBase}.subtitle`);
  const badge = t(route.badgeKey);
  const audience = t(`${translationBase}.audience`);
  const planName = t(`pricing:plans.${route.planCode}.name`);
  const primaryCta = t(route.primaryCtaKey);
  const secondaryCta = t(route.secondaryCtaKey);
  const pains = readStringList(
    t(`${translationBase}.pains`, { returnObjects: true }),
    `${translationBase}.pains`
  );
  const workflow = readStringList(
    t(`${translationBase}.workflow`, { returnObjects: true }),
    `${translationBase}.workflow`
  );
  const notFor = readStringList(
    t(`${translationBase}.notFor`, { returnObjects: true }),
    `${translationBase}.notFor`
  );
  const faqs = readFaqList(
    t(`${translationBase}.faqs`, { returnObjects: true }),
    `${translationBase}.faqs`
  );
  const includedModules = useMemo(
    () => resolveIncludedModules(route, t),
    [route, t]
  );
  const primaryHref =
    route.primaryCta === 'freeRegister'
      ? resolveCrmRegisterUrl(
          import.meta.env.VITE_CRM_REGISTER_URL,
          import.meta.env.VITE_CRM_LOGIN_URL
        )
      : '#segment-demo-form';

  usePageMetadata({
    title: `${title} | Inmueble.red`,
    description,
    path: route.path,
  });

  useStructuredData({
    id: `segment-${route.segmentCode}`,
    data: buildSegmentStructuredData({
      route,
      title,
      description,
      planName,
      faqs,
    }),
  });

  return (
    <div className="flex flex-col bg-brand-bg">
      <MarketingPageHero
        badge={badge}
        title={title}
        subtitle={subtitle}
        actions={
          <>
            <a href={primaryHref} className={homeButtonClasses('accent', 'lg')}>
              {primaryCta}
            </a>
            <Link
              to={route.secondaryHref}
              className={homeButtonClasses('outline', 'lg')}
            >
              {secondaryCta}
            </Link>
          </>
        }
        className="overflow-hidden"
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="rounded-[1.75rem] border border-brand-primary/10 bg-white p-8">
            <p className={marketingPlanBadgeClass}>
              {t('segments:common.audience')}
            </p>
            <p className="mt-5 text-xl leading-relaxed text-brand-primary">
              {audience}
            </p>
          </section>
          <section className="rounded-[1.75rem] border border-brand-primary/10 bg-brand-primary p-8 text-white">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/70">
              Plan
            </p>
            <h2 className="mt-4 font-serif text-4xl font-bold">{planName}</h2>
            <p className="mt-5 text-sm leading-relaxed text-white/65">
              {t(`pricing:plans.${route.planCode}.description`)}
            </p>
          </section>
        </div>
      </MarketingPageHero>

      <section className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            <section>
              <HomeSectionHeader
                badge={badge}
                title={t('segments:common.pains')}
                subtitle={description}
                className="mb-8"
              />
              <div className="space-y-4">
                {pains.map((pain) => (
                  <div
                    key={pain}
                    className="flex gap-4 border-b border-brand-primary/10 pb-4"
                  >
                    <XCircle
                      className="mt-0.5 shrink-0 text-[var(--ir-terra)]"
                      size={22}
                    />
                    <p className="text-lg leading-relaxed text-[var(--ir-muted)]">
                      {pain}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <HomeSectionHeader
                badge={t('segments:common.workflow')}
                title={t('segments:common.workflow')}
                subtitle={t(`${translationBase}.upgradeBody`)}
                className="mb-8"
              />
              <ol className="space-y-4">
                {workflow.map((step, index) => (
                  <li
                    key={step}
                    className="grid grid-cols-[auto_1fr] gap-4 border-b border-brand-primary/10 pb-4"
                  >
                    <span className="font-serif text-3xl italic text-[var(--ir-terra)]">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <p className="text-lg leading-relaxed text-brand-primary">
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </div>
      </section>

      <section className={HOME_SURFACES.ivory}>
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('segments:common.includes')}
            title={t('segments:common.includes')}
            subtitle={t(`pricing:plans.${route.planCode}.description`)}
            className="mb-10"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {includedModules.map((module) => (
              <article
                key={module.code}
                className="border-l border-brand-primary/20 pl-5"
              >
                <CheckCircle2
                  className="mb-5 text-[var(--ir-forest)]"
                  size={24}
                />
                <h3 className="text-xl font-bold text-brand-primary">
                  {module.name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--ir-muted)]">
                  {module.value}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={HOME_SURFACES.deep}>
        <div className="section-padding">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <section>
              <HomeSectionHeader
                badge={t('segments:common.notFor')}
                light
                title={t('segments:common.notFor')}
                subtitle={t(`${translationBase}.upgradeTitle`)}
                className="mb-8"
              />
              <div className="space-y-4">
                {notFor.map((item) => (
                  <p
                    key={item}
                    className="border-b border-white/10 pb-4 text-lg leading-relaxed text-white/65"
                  >
                    {item}
                  </p>
                ))}
              </div>
            </section>
            <section className="rounded-[1.75rem] border border-white/10 bg-white/[0.06] p-8">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--ir-mint)]">
                {t('segments:common.upgrade')}
              </p>
              <h2 className="mt-5 font-serif text-4xl font-bold text-white">
                {t(`${translationBase}.upgradeTitle`)}
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-white/65">
                {t(`${translationBase}.upgradeBody`)}
              </p>
            </section>
          </div>
        </div>
      </section>

      <section className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('segments:common.faq')}
            title={t('segments:common.faq')}
            subtitle={description}
            className="mb-10"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {faqs.map((faq) => (
              <article
                key={faq.question}
                className={cn(marketingCardClass, 'p-7')}
              >
                <HelpCircle
                  className="mb-5 text-[var(--ir-terra)]"
                  size={24}
                  aria-hidden="true"
                />
                <h3 className="text-xl font-bold text-brand-primary">
                  {faq.question}
                </h3>
                <p className="mt-4 leading-relaxed text-[var(--ir-muted)]">
                  {faq.answer}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {isPaidPlanCode(route.planCode) ? (
        <section id="segment-demo-form" className={HOME_SURFACES.ivory}>
          <div className="section-padding">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.85fr_1fr]">
              <HomeSectionHeader
                badge={t('segments:common.contactBadge')}
                title={t('segments:common.contactTitle')}
                subtitle={t('segments:common.contactSubtitle')}
              />
              <div className="rounded-[1.75rem] border border-brand-primary/10 bg-white p-8">
                <DemoRequestForm
                  appearance="underlined"
                  submitLabel={primaryCta}
                  helperText={t('pricing:demoHelperText')}
                  defaultPlanInterest={route.planCode}
                />
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}

export function ProductRoutePage(
  props: ProductRoutePageProps
): React.JSX.Element {
  if (props.segmentCode) {
    return <SegmentProductPage segmentCode={props.segmentCode} />;
  }

  return <LegacyProductPage routeKey={props.routeKey} />;
}
